import { USAGE_DISPLAY, USAGE_OFFER } from '@/data/constants/catalog';
import { TYPES } from '@/data/constants/product';
import { Price, ProductDisplayPrice, ResponseProductType } from '@/data/types/Product';
import { dFix } from '@/data/utils/floatingPoint';
import {
	PRODUCT_PRICE_USAGE,
	ProductQuantityMeasures,
	ProductWeightMeasures,
} from 'shared/custom/constants/commonConstants';

interface PriceUnits {
	singleUnit: string;
	wholeUnit: string;
}

const QUANTITY_UNITS = {
	[ProductQuantityMeasures.SQUARE_METER]: 'm2',
	[ProductQuantityMeasures.METER]: 'm',
	[ProductQuantityMeasures.KILOGRAM]: 'kg',
	[ProductQuantityMeasures.LITER]: 'l',
};

const WEIGHT_UNITS = {
	[ProductWeightMeasures.BOX]: 'Por caja',
	[ProductWeightMeasures.LINEAR_METER]: 'Por metro lineal',
};

const getPriceUnits = (quantityMeasure?: string, weightMeasure?: string): PriceUnits => {
	const isMeterBased =
		quantityMeasure === ProductQuantityMeasures.SQUARE_METER ||
		quantityMeasure === ProductQuantityMeasures.METER;

	const singleUnit = quantityMeasure ? QUANTITY_UNITS[quantityMeasure] || '' : '';
	let wholeUnit = '';

	if (isMeterBased) {
		wholeUnit = weightMeasure ? WEIGHT_UNITS[weightMeasure] || '' : '';
	}

	return { singleUnit, wholeUnit };
};

// eslint-disable-next-line complexity
const getPrices = (quantityMeasure: string, weightMeasure: string, offerPriceObj?: Price) => {
	if (!offerPriceObj) {
		return { offerPrice: 0, listPrice: 0, wholeOffer: 0, wholeList: 0 };
	}
	const isSpecialMeasure =
		(quantityMeasure === ProductQuantityMeasures.SQUARE_METER ||
			quantityMeasure === ProductQuantityMeasures.METER) &&
		(weightMeasure === ProductWeightMeasures.BOX ||
			weightMeasure === ProductWeightMeasures.LINEAR_METER);

	const { value, field1 } = offerPriceObj;

	if (isSpecialMeasure) {
		return {
			offerPrice: dFix(field1?.OMP ?? value ?? 0),
			wholeOffer: dFix(value ?? 0),
			listPrice: dFix(field1?.LMP ?? 0),
			wholeList: dFix(field1?.LP ?? 0),
		};
	}

	return {
		offerPrice: dFix(value ?? 0),
		listPrice: dFix(field1?.LP ?? 0),
		wholeOffer: 0,
		wholeList: 0,
	};
};

const getKitListPrice = (
	components: ResponseProductType['components'],
	productType: ResponseProductType['type']
): number => {
	const targetUsage =
		productType === TYPES.kit ? PRODUCT_PRICE_USAGE.DISPLAY : PRODUCT_PRICE_USAGE.OFFER;
	return (
		components?.reduce((acc, { price, quantity }) => {
			const componentPrice = price.find(({ usage }) => usage === targetUsage);
			const value = componentPrice?.value ? parseFloat(componentPrice.value) : 0;
			const qty = quantity ? parseFloat(quantity) : 0;
			return value > 0 && qty > 0 ? acc + value * qty : acc;
		}, 0) ?? 0
	);
};

const getMeasurement = (product: ResponseProductType) => {
	const quantityMeasure = product['x_measurements.quantityMeasure'] || 'C62';
	const weightMeasure = product['x_measurements.weightMeasure'] || '';
	return { quantityMeasure, weightMeasure };
};

export const getProductPrice = (product: ResponseProductType): ProductDisplayPrice => {
	const { price, components } = product;
	const offer = price?.find(({ usage: u }) => u === USAGE_OFFER);
	const list = price?.find(({ usage: u }) => u === USAGE_DISPLAY);
	const { quantityMeasure, weightMeasure } = getMeasurement(product);
	const { singleUnit, wholeUnit } = getPriceUnits(quantityMeasure, weightMeasure);
	const { offerPrice, listPrice, wholeOffer, wholeList } = getPrices(
		quantityMeasure,
		weightMeasure,
		offer
	);
	const kitBundlePrices =
		components?.map((c) => {
			const componentOffer = c.price?.find((p) => p.usage === USAGE_OFFER) ?? c.price?.[0];
			const priceValue = parseFloat(componentOffer?.value || '0');
			const qty = parseFloat(c.quantity || '1');
			return priceValue * qty;
		}) ?? [];
	let kitListPrice = 0;
	let minBundlePrice = 0;
	let maxBundlePrice = 0;

	if (components && components.length > 0 && kitBundlePrices) {
		const validPrices = kitBundlePrices.filter((v) => v > 0);
		minBundlePrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
		maxBundlePrice = kitBundlePrices.reduce((acc, curr) => acc + curr, 0);
		kitListPrice = getKitListPrice(components, product?.type);
	}
	const currency = offer?.currency ?? list?.currency;
	const listPriceToUse = dFix(listPrice || kitListPrice);
	const normalizedOffer = dFix(offerPrice || '0');
	const savings = dFix(listPriceToUse - offerPrice);

	return {
		minBundlePrice,
		maxBundlePrice,
		min: normalizedOffer,
		max: 0,
		currency,
		offer: normalizedOffer,
		savings: savings > 0 ? savings : 0,
		list: listPriceToUse,
		wholeOffer,
		wholeList,
		singleUnit,
		wholeUnit,
	};
};
